import React, { useEffect, useMemo, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import ICoverageArea from "../../interfaces/ICoverageArea";
import MapCoverage from "./MapCoverage";
import SearchBar from "../atoms/SearchBard";
import { Box, Divider, useMediaQuery } from "@mui/material";
import CoverageList from "./CoverageList";
import { NavCoverage } from "./NavCoverage";
import { AddCoverage } from "./AddCoverage";
import { colors } from "../../../ui/colors";
import { useModals } from "../../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { useCoverageLogic } from "../../hooks/useCoverageLogic";

const DEFAULT_MAP_CENTER = { lat: 40.7128, lng: -74.006 };

export const CoveragePage: React.FC = () => {
  const isXs = useMediaQuery("(max-width:600px)");
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const { areas: serverAreas, search: searchTerm, setSearch: setSearchTerm, handleAdd, handleUpdate, handleDelete, isLoading } = useCoverageLogic();
  const { showDialog, modals, openModal } = useModals();
  const { showSnackbar } = useGlobalSnackbar();

  const [areaToEdit, setAreaToEdit] = useState<ICoverageArea | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);

  const finalFiltered = useMemo(() => serverAreas.filter((a) => a.name.toLowerCase().includes(searchTerm.toLowerCase())), [serverAreas, searchTerm]);

  useEffect(() => {
    if (serverAreas.length > 0) {
      if (selectedId === null) {
        setMapCenter({ lat: serverAreas[0].latitude, lng: serverAreas[0].longitude });
      }
    } else {
      setMapCenter(DEFAULT_MAP_CENTER);
      setSelectedId(null);
    }
  }, [serverAreas, selectedId]);

  useEffect(() => {
    const coverageModalOpen = modals.some((modal) => modal.key === "Organization-CoverageAreas");
    if (!coverageModalOpen) setAreaToEdit(null);
  }, [modals]);

  const handleSaveArea = async (data: ICoverageArea) => {
    try {
      if (data.id) {
        await handleUpdate(data);
        showSnackbar("Area updated successfully!", "success");
      } else {
        await handleAdd(data);
        showSnackbar("New area created!", "success");
      }
      setAreaToEdit(null);
    } catch (error) {
      showSnackbar("Error saving area: " + error, "error");
    }
  };

  const handleSelectArea = (id: number) => {
    setSelectedId(id);
    const selected = serverAreas.find((a) => a.id === id);
    if (selected) setMapCenter({ lat: selected.latitude, lng: selected.longitude });
  };

  const handleDeleteArea = (id: number) => {
    showDialog({
      type: "confirm",
      title: "Delete Area",
      message: "Are you sure you want to delete this area?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        try {
          await handleDelete(id);
          showSnackbar("Area deleted successfully!", "success");
        } catch (error) {
          showSnackbar("Error deleting area: " + error, "error");
        }
      },
    });
  };

  if (!isLoaded || isLoading) return null;

  const MapSection = (
    <Box
      sx={{ mb: 1, flex: 3, borderRadius: "12px", overflow: "hidden", border: 1, borderColor: colors.gray[100], minHeight: isXs ? "300px" : "auto" }}
    >
      <MapCoverage key={finalFiltered.length} areas={finalFiltered} center={mapCenter} />
    </Box>
  );

  const ListSection = (
    <Box
      sx={{
        flex: 1.7,
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        borderRadius: "12px",
        border: 1,
        borderColor: colors.gray[100],
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1 }}>
        <SearchBar
          value={searchTerm}
          placeholder="Search areas..."
          onChange={setSearchTerm}
          onSave={handleSaveArea}
          initialData={areaToEdit}
          onCancelEdit={() => setAreaToEdit(null)}
        />
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        <CoverageList
          coverageAreas={finalFiltered}
          selectedId={selectedId}
          onSelect={handleSelectArea}
          onDelete={handleDeleteArea}
          onUpdate={(updatedArea: ICoverageArea) => handleUpdate(updatedArea)}
          onRadiusChange={(id, miles) => handleUpdate({ id, milesOfCoverageArea: miles })}
          onColorChange={(id, col) => handleUpdate({ id, color: col })}
          onOpenEditMode={(area) => {
            setAreaToEdit(area);
            openModal(
              { key: "Organization-CoverageAreas", title: "Edit Coverage Area" },
              <AddCoverage key={area.id} onSave={handleSaveArea} initialData={area} onCancelEdit={() => setAreaToEdit(null)} />,
            );
          }}
          isSearchActive={searchTerm.length > 0}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 2, height: "100vh", backgroundColor: "var(--fx-surface-muted, #F1F5F9)", display: "flex", flexDirection: "column" }}>
      <NavCoverage
        onSave={handleSaveArea}
        totalAreas={finalFiltered.length}
        areaToEdit={areaToEdit}
        onOpen={() => setAreaToEdit(null)}
        onCancelEdit={() => setAreaToEdit(null)}
      />

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: isXs ? "column" : "row",
          height: "calc(100% - 80px)",
          mt: 1,
        }}
      >
        {isXs ? (
          <>
            {ListSection}
            {MapSection}
          </>
        ) : (
          <>
            {MapSection}
            {ListSection}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CoveragePage;
